import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Code, Group, Stack, Text, Skeleton as MSkeleton } from '@mantine/core'
import {
  IconAlertTriangle, IconHelpCircle, IconPlugConnected, IconPlugConnectedX,
} from '@tabler/icons-react'
import {
  PageTitle, SectionAction, SectionCard, SectionTitle, StatTile, StatusBadge, EmptyState, MetaList,
  HairlineDivider,
} from '@lighthouse/design'
import type { StatusRole } from '@lighthouse/design'
import {
  contactHistoryLabel, dashInstruction, detailTone, failureCountLabel, faultKindLabel,
  groupByConnector, inventoryLabel, latencyLabel, observedViaLabel, permanentFaultLabel,
  usableLabel, withheldFields,
} from '../components/sources/sourceFormat'
import { useSourceHealth } from '../hooks/useOverview'
import { LinkedFromSignal, LinkedMark } from '../components/signals/LinkedFromSignal'
import { isLinked, linkedKey } from '../utils/deepLink'
import { NOT_MEASURED, agoLabel, errorSummary, exactNumber, whenLabel } from '../utils/format'
import type { SourceHealth, SourceHealthOverview } from '../types/observatory'

/**
 * Sources — whether each upstream source is answering, per host.
 *
 * `GET api/observatory/sources` has been live and consumed by nothing since the
 * source-health projection shipped. This page is that endpoint and no more: no
 * controller, no DTO, no migration, and nothing recomputed. It replaces a check
 * that reported Oracle as **Healthy from a process that had never loaded the
 * Oracle driver** — it discovered the driver's absence by scanning loaded
 * assemblies and called that green.
 *
 * Five decisions shape it, and four of them exist to stop this page putting that
 * fiction back by a side door:
 *
 *  1. **An unobserved source is never green.** `status` is nullable, `null` is a
 *     dash, and no fourth status value was invented — a non-answer given its own
 *     colour has quietly become an answer. The dash always keeps its caption,
 *     because "Not observed" and "Not configured" are the same glyph with
 *     opposite next steps, and the dash is the reading a reader is least able to
 *     guess at.
 *  2. **`hasNeverReported` is read FIRST, before staleness.** The never-reported
 *     envelope carries `isProjectionStale: false`, `observedAt: null` and all
 *     three counts at zero, so a page that asks about staleness first renders an
 *     empty projection as healthy and fresh. Same trap as the worker fleet, same
 *     branch order.
 *  3. **No percentage, anywhere.** A reachability rate is the obvious headline
 *     and it is the wrong one: the honest normal state here is one source that
 *     nothing has contacted today, which divides to `0%` and reads as "nothing
 *     is reachable" when the truth is "nothing has looked". The three counts
 *     already partition the list exactly; they are rendered as counts.
 *  4. **Withheld is not zero, and not false.** Three fields come back `null`
 *     when the server has no current knowledge, and on the same row one can be
 *     withheld while two are present. Rendering them all as dashes, or all as
 *     values, both misreport — so each is rendered from the wire and the page
 *     says which ones were withheld and why.
 *  5. **Grouped by connector, with the host named.** One row today. The whole
 *     point of the composite key is that four rows for one connector read as
 *     "three of four hosts can reach Oracle" — a firewall problem, not an Oracle
 *     problem — so the grouping is here from day one and server order is
 *     preserved rather than sorted.
 *
 * **Form: a card per row, and the reason does not generalise.** Cards suit this
 * because there are one to a handful of rows and because the `detail` sentence
 * is the single highest-value field on the page — a table wide enough to hold it
 * would truncate it, and truncating it is what makes "not observed" read as a
 * broken page. `/connections` is up to ~6,300 rows over thirty days and wants a
 * paged table with the ledger's filters; whoever builds it should copy the
 * reasoning, not the layout.
 *
 * **Not to be confused with `/connections`, which is a different page.** This one
 * answers "can we reach it, right now" from a projection that is overwritten every
 * sixty seconds and keeps no history. `/connections` (doc 15, *Moorings*) is the
 * per-connection ledger over `Etl.EtlRunChunk` — every connection opened and
 * closed over thirty days, with its errors and traffic. Current state versus
 * history, and neither substitutes for the other: this page cannot tell you what
 * happened on Tuesday, and the ledger cannot tell you whether Oracle is answering
 * now. So the two are one click apart rather than merged: every connector group
 * below ends in a "Connection history" pivot, and the ledger's header points back
 * here. The pivot carries no filter, because the ledger has none to take — no host
 * filter exists, and the connector is a read-time constant server-side — so it
 * says so rather than implying it narrows to the card it sits under.
 */
export function SourcesPage() {
  const { data, isLoading, isError, error } = useSourceHealth()
  // `?connector=`: the Signal Inbox's link to one source. Its group is shown first and marked; a connector no host
  // reports now is said to be missing rather than silently dropped.
  const [search] = useSearchParams()
  const linked = linkedKey(search, 'connector')

  if (isError) {
    // The server's own sentence, when it gave one, because it is the only thing
    // on this panel that tells the three causes below apart without a console.
    const said = errorSummary(error)
    return (
      <EmptyState
        icon={<IconPlugConnectedX size={28} stroke={1.5} />}
        title="Could not load source health"
        message={
          <>
            {'The request for source health did not come back. That is a fault between this page and the '
            + 'API — it says nothing about whether any source is reachable. The likeliest cause on a fresh '
            + 'database is migration AddSourceConnectionHealth not having been applied, since migrations '
            + 'here are applied by hand and never by the app; but a rejected sign-in and an API that is not '
            + 'running land here identically. The browser console distinguishes them.'}
            {said && <> The request said: {said}</>}
          </>
        }
      />
    )
  }

  if (isLoading || !data) {
    return (
      <Stack gap="md">
        <MSkeleton height={140} />
        <MSkeleton height={320} />
      </Stack>
    )
  }

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle="Whether each upstream source is answering, which host asked, and what it last said">
        Sources
      </PageTitle>

      {/*
        Branch order is load-bearing, not stylistic. See decision 2 above: the
        never-reported envelope looks fresh and healthy on every other field.
      */}
      {data.hasNeverReported ? (
        <NeverReported />
      ) : (
        <>
          {data.isProjectionStale && <ProjectionStale observedAt={data.observedAt} />}
          <SourceSummary overview={data} />
          <LinkedConnectorGroups groups={groupByConnector(data.sources)} linked={linked} />
        </>
      )}
    </Stack>
  )
}

/**
 * No host has ever published a source reading.
 *
 * Deliberately not the same as every source being down, and the wording says so:
 * a source that is down leaves a row behind saying it is down. This is the
 * minute after the migration lands and before Automaton's first sixty-second
 * pass, or a database no worker is pointed at — two findings with different next
 * steps that a bare "no data" would flatten into one.
 */
function NeverReported() {
  return (
    <EmptyState
      icon={<IconPlugConnectedX size={28} stroke={1.5} />}
      title="No host has ever reported on a source"
      message={
        'Automaton publishes one row per connector every 60 seconds once it is running, and nothing has '
        + 'been recorded here. That is not every source being down — a source that is down leaves a row '
        + 'behind saying so. Either no worker is pointed at this database, or the projection table was '
        + 'created within the last minute.'
      }
    />
  )
}

/**
 * The projection is stale — nothing is publishing these rows.
 *
 * A different failure from a source going quiet, and the two need opposite
 * responses: a stale `lastSuccessAt` under a fresh observation means a SOURCE
 * went quiet, and a stale observation means the OBSERVER stopped and the sources
 * may be perfectly fine. It is stated rather than left to be inferred from a
 * timestamp, because it invalidates every figure below including the reassuring
 * ones — and it is also the reason two measurements go missing from each card,
 * which is said here rather than left as an unexplained pair of dashes.
 */
function ProjectionStale({ observedAt }: { observedAt: string | null }) {
  return (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      color="yellow"
      variant="light"
      title="These readings are history, not status"
    >
      <Text size="sm">
        The last source reading was published {agoLabel(observedAt)}, so the observer has stopped and
        every row below is served as unknown whatever it last said. The sources themselves may be entirely
        healthy; what is known to be broken is the reporting. While this holds, each row withholds its
        failure count and whether contact has ever been attempted — a running total whose clock has
        stopped is not history, it is a partial measurement that would read as a current one.
      </Text>
    </Alert>
  )
}

/**
 * The three counts, exactly as served.
 *
 * They partition the list — usable, faulted, unobserved — and are taken off the
 * derived values server-side so the header cannot disagree with its own rows.
 * The page renders them and adds neither a fourth count nor a ratio.
 */
function SourceSummary({ overview }: { overview: SourceHealthOverview }) {
  return (
    <SectionCard
      title="What is answering"
      subtitle={
        overview.observedAt
          ? `${inventoryLabel(overview.sources)} · last published ${whenLabel(overview.observedAt)}`
          : inventoryLabel(overview.sources)
      }
    >
      <Stack gap="md">
        <div
          className="lh-overview-grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
        >
          {/*
            No reachability percentage here, and none anywhere on this page. Over
            the honest normal state — one source that nothing has contacted today
            — usable over total is 0/1, and a headline "0% reachable" says
            nothing is up when the truth is that nothing has looked. That is the
            precise inversion this surface exists to delete, arriving as the
            biggest number on the screen. The partition is rendered as three
            counts instead, which cannot be read as a rate.

            Only the faulted tile takes a status role. A measured zero is printed
            plainly rather than decorated as good, and "not observed" is given no
            colour at all, because a non-answer with its own tone has become an
            answer.

            The staleness half of the faulted tile's guard is belt-and-braces and
            is documented as such rather than sold as a feature: the envelope's
            staleness is measured on the NEWEST row, so when it is true every row
            is at least that old, every row takes the first rung of the server's
            ladder, and faultedCount is already zero. The guard therefore never
            fires today. It is kept because it costs nothing and it states the
            intent — a stale projection cannot tell a failing source from a silent
            publisher — but a reader should not believe it is what delivers that.
          */}
          <StatTile
            label="Usable"
            value={exactNumber(overview.usableCount)}
            sublabel="reachable or degraded"
          />
          <StatTile
            label="Faulted"
            value={exactNumber(overview.faultedCount)}
            status={overview.faultedCount > 0 && !overview.isProjectionStale ? 'critical' : undefined}
            sublabel="unreachable, or refusing us"
          />
          <StatTile
            label="Not observed"
            value={exactNumber(overview.unobservedCount)}
            sublabel="nothing has looked, or no connector here"
          />
        </div>

        <HairlineDivider />

        {/*
          Standing copy, shown in every state including the healthy one. Without
          it a page whose normal reading is a row of dashes looks broken, and a
          page that looks broken in its normal state gets ignored in its abnormal
          one. This is also the sentence the deleted health check could not say.
        */}
        <Text size="xs" c="dimmed">
          Lighthouse learns about a source from the ETL&rsquo;s own traffic. Every ETL job is
          manual-trigger, and the active dial that would open a connection when nothing else has is a flag
          that ships off, so on a day nobody runs a load nothing contacts Oracle at all. A dash here is
          that day being reported honestly: no evidence is served as no evidence rather than as health.
          The check this replaced answered &ldquo;healthy&rdquo; from a process that had never loaded the
          Oracle driver, which is the reading a dash exists to refuse.
        </Text>
      </Stack>
    </SectionCard>
  )
}

/**
 * One connector, across every host that reported on it.
 *
 * The band appears only when more than one host reported, because that is the
 * only time there is anything to compare — but the grouping itself is
 * unconditional, so a second Automaton instance changes what this renders and
 * not how the page is built. Rows keep the server's order within the group.
 *
 * The pivot to the ledger is per connector, not per host card, and it is
 * unconditional for the same reason the grouping is.
 */
/** The connector groups in served order — except that a linked group comes first — with the link's notice. */
function LinkedConnectorGroups({
  groups,
  linked,
}: {
  groups: ReturnType<typeof groupByConnector>
  linked: string | null
}) {
  const match = linked ? groups.find((g) => isLinked(g.connectorId, linked)) ?? null : null
  const ordered = match ? [match, ...groups.filter((g) => g !== match)] : groups
  return (
    <>
      {linked && <LinkedFromSignal what="source connector" id={linked} found={match !== null} />}
      {ordered.map((group) => (
        <ConnectorGroup key={group.connectorId} connectorId={group.connectorId} rows={group.rows} linked={group === match} />
      ))}
    </>
  )
}

function ConnectorGroup({ connectorId, rows, linked = false }: { connectorId: string; rows: SourceHealth[]; linked?: boolean }) {
  const navigate = useNavigate()

  return (
    <Stack gap="md" data-connector-group={connectorId}>
      {linked && <LinkedMark />}
      {rows.length > 1 && (
        <SectionTitle
          subtitle={
            `${rows.length} hosts reported on this source. They are allowed to disagree, and a `
            + 'disagreement is the finding: some hosts reaching it and others not is a network or '
            + 'firewall problem rather than a problem with the source.'
          }
        >
          <Code>{connectorId}</Code>
        </SectionTitle>
      )}
      {rows.map((source) => (
        <SourceCard key={`${source.connectorId}::${source.hostName}`} source={source} />
      ))}
      {/*
        The other half of "current state versus history": this card cannot say
        what happened on Tuesday, and the ledger can.

        onClick + navigate, never `href` — SectionAction's href renders a bare
        <a>, and a bare <a> inside BrowserRouter is a full page reload.

        It navigates to the UNFILTERED ledger, and the caption says so rather
        than letting the placement imply otherwise. The ledger has no host filter
        at all, and its connector is a constant the API stamps on every row at
        read time rather than a column it can filter on — so a link that looked
        like "this host's connections to this source" would be promising a
        narrowing that does not happen, and the reader would take the whole
        ledger for one host's share of it.
      */}
      <Group justify="space-between" align="center" gap="xs" wrap="wrap">
        <Text size="xs" c="dimmed" style={{ flex: '1 1 320px', minWidth: 0 }}>
          What happened, rather than what is true now: every connection the ETL opened over the last 30
          days. It opens on the whole ledger — the ledger is not filtered by host, and it has no
          connector filter either.
        </Text>
        <SectionAction onClick={() => navigate('/connections')}>
          Connection history →
        </SectionAction>
      </Group>
    </Stack>
  )
}

/**
 * One source as one host sees it.
 *
 * `detail` leads, at full width and never in a tooltip. It is the sentence the
 * server composed for exactly this position and it differs by which rung of the
 * derivation fired — never observed, idle for seven hours, the observer stopped,
 * the connection string cannot be parsed — so it is the single field that turns
 * "not observed" from an apparent gap into a stated finding. Hiding it behind a
 * hover is what would make this page read as unfinished.
 */
function SourceCard({ source }: { source: SourceHealth }) {
  const instruction = dashInstruction(source.reachability, source.isProjectionStale)
  const withheld = withheldFields(source)

  return (
    <SectionCard
      title={source.displayName || source.connectorId}
      subtitle={`${source.connectorId} · ${source.sourceSystem} · observed from ${source.hostName}`}
      action={<SourceStatus status={source.status} statusLabel={source.statusLabel} />}
    >
      <Stack gap="md">
        <Alert
          color={detailTone(source.status)}
          variant="light"
          icon={<DetailIcon status={source.status} />}
          title={source.statusLabel}
        >
          <Stack gap={6}>
            <Text size="sm">{source.detail}</Text>
            {/*
              The next step behind the dash, which the detail sentence above
              states the cause of but never the response to. Unknown and
              NotConfigured share a glyph and mean opposite things — "wait" and
              "stop asking" — and within Unknown, a stale projection needs the
              opposite advice from an idle one: see dashInstruction, which takes
              the staleness flag for exactly that reason.
            */}
            {instruction && <Text size="sm" fw={500}>{instruction}</Text>}
          </Stack>
        </Alert>

        <MetaList
          items={[
            { label: 'Host', value: source.hostName, mono: true },
            { label: 'Connector', value: source.connectorId, mono: true },
            { label: 'Source system', value: source.sourceSystem },
            { label: 'Reachability', value: source.reachability },
            { label: 'Observed via', value: observedViaLabel(source.observedVia) },
            { label: 'Usable', value: usableLabel(source.isUsable) },
            { label: 'Permanent fault', value: permanentFaultLabel(source.isPermanentFault) },
            { label: 'Consecutive failures', value: failureCountLabel(source.consecutiveFailures) },
            { label: 'Ever contacted', value: contactHistoryLabel(source.hasNeverBeenContacted) },
          ]}
        />

        <HairlineDivider />

        <Stack gap="xs">
          <Text size="sm" fw={500}>History</Text>
          <MetaList
            items={[
              // Three timestamps rather than two, and the distinction is the
              // question the feature exists to answer: a stale last success
              // under a fresh observation means the SOURCE went quiet, while a
              // stale observation means the OBSERVER stopped. `lastAttemptAt`
              // advances even when the circuit breaker refuses the call without
              // dialling, which is what separates "Oracle is down" from "nobody
              // has asked Oracle anything since Tuesday".
              { label: 'This row published', value: whenLabel(source.observedAt) },
              { label: 'Last attempt', value: whenLabel(source.lastAttemptAt) },
              { label: 'Last success', value: whenLabel(source.lastSuccessAt) },
              { label: 'Last failure', value: whenLabel(source.lastFailureAt) },
              { label: 'Last fault kind', value: faultKindLabel(source.lastFaultKind) },
              { label: 'Last successful call took', value: latencyLabel(source.lastLatencyMs) },
            ]}
          />
        </Stack>

        {/*
          The dated diagnosis, kept after recovery on purpose: "it is up now, and
          four hours ago it was refusing our credentials" is a different report
          from "it is up". It can therefore sit beside a fault kind of None
          without either being wrong, which is stated rather than left to look
          like a contradiction.
        */}
        {source.lastFailureDetail && (
          <>
            <HairlineDivider />
            <Stack gap="xs">
              <Text size="sm" fw={500}>What the last failure said</Text>
              <Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {source.lastFailureDetail}
              </Code>
              <Text size="xs" c="dimmed">
                Recorded {agoLabel(source.lastFailureAt)} and kept after any later success. A success
                resets the fault kind above to None, and so does a worker restart — so this text standing
                beside &ldquo;None recorded&rdquo; means the failure happened, at that time, and nothing
                since has confirmed or contradicted it.
              </Text>
            </Stack>
          </>
        )}

        {/*
          Rendered only when something actually is withheld. On the honest normal
          state one derived flag is withheld while two measurements are present
          and read true and zero, so a blanket note would be as wrong as none.
        */}
        {withheld.length > 0 && (
          <Text size="xs" c="dimmed">
            Withheld above, not measured as zero or false: {withheld.join(', ')}.{' '}
            {source.isProjectionStale
              ? 'Nothing is publishing this row, so its running counts have no "as of" and would read as '
                + 'current if printed.'
              : 'The server has no current verdict on this source, and advice issued while admitting '
                + 'ignorance is worse than none — a confident "no permanent fault" reads exactly like a '
                + 'cleared one.'}
          </Text>
        )}
      </Stack>
    </SectionCard>
  )
}

/**
 * The glyph on the detail alert, which must not out-argue the colour beside it.
 *
 * The obvious mapping is backwards, and it shipped that way for a day: keying on
 * `status === null` gives the UNOBSERVED row a severed plug and the genuinely
 * UNREACHABLE row an intact one — the two readings this page exists to keep
 * apart, illustrated as each other's opposite.
 *
 * So a null takes a question mark. `detailTone` sends it to a neutral grey
 * precisely so the non-answer stays a non-answer, and handing that same row a
 * "broken" glyph would re-supply in the icon slot the verdict the colour had just
 * withheld. An icon is a claim like any other.
 */
function DetailIcon({ status }: { status: StatusRole | null }) {
  switch (status) {
    case 'good': return <IconPlugConnected size={16} />
    case 'warning': return <IconAlertTriangle size={16} />
    case 'serious':
    case 'critical': return <IconPlugConnectedX size={16} />
    default: return <IconHelpCircle size={16} />
  }
}

/**
 * The status badge, or the dash and its caption.
 *
 * `StatusBadge` takes a non-nullable `StatusRole` by design — the union was
 * deliberately NOT widened with an 'unknown' member, because both SPAs consume
 * it and every existing switch would go non-exhaustive for the sake of one row.
 * So the absence is rendered here instead, as an absence: a dash is not a fourth
 * status value, it is the lack of one.
 *
 * The caption survives the dash and is never dropped. `statusLabel` is always
 * populated server-side, including for the two unobserved states, precisely
 * because a dash needs its caption more than a green tick does.
 */
function SourceStatus({ status, statusLabel }: { status: StatusRole | null; statusLabel: string }) {
  if (status) return <StatusBadge status={status} label={statusLabel} />

  return (
    <Group gap={6} wrap="nowrap" align="center">
      <Text span c="dimmed" aria-hidden="true">{NOT_MEASURED}</Text>
      <Text span size="sm" c="dimmed">{statusLabel}</Text>
    </Group>
  )
}
